import {
  ClientCasesPageSchema,
  ContactConversionSchema,
  DoctorPageSchema,
  DoctorPatientsSchema,
  DoctorSchema,
  DoctorSummarySchema,
  PatientPageSchema,
  PatientSchema,
  PatientSummarySchema,
  SimilarDoctorsSchema,
} from '@contracts/schemas/client.schema';
import type {
  ClientCasesPage,
  ClientRepository,
  DoctorCasesExport,
  DoctorFilter,
  DoctorPage,
  DoctorPatientsExport,
  DoctorPatientsPage,
  MergeDoctorCommand,
  NewPatient,
  PatientFilter,
  PatientPage,
  RenamePatientCommand,
  SaveDoctorCommand,
} from '@/application/ports/ClientRepository';
import {
  clientCaseQuery,
  doctorPatientsQuery,
  type ClientCaseFilter,
  type ContactConversion,
  type Doctor,
  type DoctorInput,
  type DoctorPatientsFilter,
  type DoctorSummary,
  type Patient,
  type PatientSummary,
} from '@/domain';
import { env } from '@/shared/config/env';
import { SaidError } from '@/shared/failure/failure';
import { request, send } from '../client';
import { getAccessToken } from '../tokenStore';

/** The query of either list. An empty search and an absent filter are left off. */
function listQuery(
  filter: Omit<DoctorFilter, 'show'> & Omit<PatientFilter, 'show'> & { show?: string },
): string {
  const params = new URLSearchParams();
  if (filter.q !== undefined && filter.q.trim() !== '') params.set('q', filter.q.trim());
  if (filter.doctorId !== undefined && filter.doctorId !== '') params.set('doctorId', filter.doctorId);
  // Only ever sent as true: leaving it off is the whole list.
  if (filter.duplicates === true) params.set('duplicates', 'true');
  // The list narrowed to what one of the counts above it counts.
  if (filter.show !== undefined) params.set('show', filter.show);
  if (filter.sort !== undefined) params.set('sort', filter.sort);
  if (filter.page !== undefined) params.set('page', String(filter.page));
  if (filter.pageSize !== undefined) params.set('pageSize', String(filter.pageSize));
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

/** A filter as its query, or nothing at all when it asks for nothing. */
function queryOf(params: URLSearchParams): string {
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

/** The filter and the order on screen, without the page: an export is every row that matches. */
function unpaged<Filter extends { page?: number; pageSize?: number }>(filter: Filter): Filter {
  return { ...filter, page: undefined, pageSize: undefined };
}

/**
 * A file the server builds. CSV rather than JSON, so it bypasses the schema
 * parsing client, as the Archive's export does: the same credentials, and the
 * bytes as they come.
 */
async function download(path: string): Promise<Blob> {
  const token = getAccessToken();
  const response = await send(`${env.apiBaseUrl}${path}`, {
    credentials: 'include',
    headers: token === null ? {} : { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) throw new SaidError('The export could not be produced');
  return response.blob();
}

/** An id is a number as text; encoded all the same, since it arrives from an address. */
function at(id: string): string {
  return encodeURIComponent(id);
}

/** The name and, only when it was asked for, the switch that allows a second patient of it. */
function patientBody(name: string, allowSameName: boolean | undefined): Record<string, unknown> {
  return allowSameName === true ? { name, allowSameName: true } : { name };
}

/**
 * Doctors and patients over HTTP. The request of 6 October 2026 (second),
 * items 4 and 5, the request of 6 October 2026 (third), and the counts,
 * filters, exports and conversion of the request of 6 October 2026 (fifth).
 * None of it is queued offline: these are a desk's records, and an edit
 * replayed an hour later would overwrite whatever was typed since.
 */
export class HttpClientRepository implements ClientRepository {
  doctors(filter: DoctorFilter): Promise<DoctorPage> {
    return request(`/doctors${listQuery(filter)}`, { schema: DoctorPageSchema });
  }

  doctorSummary(): Promise<DoctorSummary> {
    return request('/doctors/summary', { schema: DoctorSummarySchema });
  }

  doctor(id: string): Promise<Doctor> {
    return request(`/doctors/${at(id)}`, { schema: DoctorSchema });
  }

  /**
   * A filter that asks for neither a page nor a page size is answered with
   * every match at once, which is what the intake form's list wants.
   */
  doctorPatients(doctorId: string, filter: DoctorPatientsFilter): Promise<DoctorPatientsPage> {
    return request(`/doctors/${at(doctorId)}/patients${queryOf(doctorPatientsQuery(filter))}`, {
      schema: DoctorPatientsSchema,
    });
  }

  async doctorCases(doctorId: string, filter: ClientCaseFilter): Promise<ClientCasesPage> {
    const result = await request(
      `/doctors/${at(doctorId)}/cases${queryOf(clientCaseQuery(filter))}`,
      { schema: ClientCasesPageSchema },
    );
    return result as unknown as ClientCasesPage;
  }

  exportDoctorCases(command: DoctorCasesExport): Promise<Blob> {
    const query = queryOf(clientCaseQuery(unpaged(command.filter)));
    return download(`/doctors/${at(command.doctorId)}/cases/export${query}`);
  }

  exportDoctorPatients(command: DoctorPatientsExport): Promise<Blob> {
    const query = queryOf(doctorPatientsQuery(unpaged(command.filter)));
    return download(`/doctors/${at(command.doctorId)}/patients/export${query}`);
  }

  contactConversion(): Promise<ContactConversion> {
    return request('/doctors/contact-conversion', { schema: ContactConversionSchema });
  }

  /** The same address as the list of what would happen, sent as a command: it does exactly that. */
  convertContacts(): Promise<ContactConversion> {
    return request('/doctors/contact-conversion', {
      method: 'POST',
      schema: ContactConversionSchema,
    });
  }

  async similarDoctors(doctorId: string): Promise<Doctor[]> {
    const result = await request(`/doctors/${at(doctorId)}/similar`, {
      schema: SimilarDoctorsSchema,
    });
    return result.items;
  }

  addDoctor(doctor: DoctorInput): Promise<Doctor> {
    return request('/doctors', { method: 'POST', body: doctor, schema: DoctorSchema });
  }

  saveDoctor(command: SaveDoctorCommand): Promise<Doctor> {
    return request(`/doctors/${at(command.id)}`, {
      method: 'PATCH',
      body: command.changes,
      rowVersion: command.rowVersion,
      schema: DoctorSchema,
    });
  }

  /** A 204: there is no record left to answer with. */
  async deleteDoctor(id: string): Promise<void> {
    await request(`/doctors/${at(id)}`, { method: 'DELETE' });
  }

  /** The doctor kept is in the address, and the answer is that doctor as it now reads. */
  mergeDoctor(command: MergeDoctorCommand): Promise<Doctor> {
    return request(`/doctors/${at(command.keepId)}/merge`, {
      method: 'POST',
      body: { duplicateId: command.duplicateId },
      schema: DoctorSchema,
    });
  }

  patients(filter: PatientFilter): Promise<PatientPage> {
    return request(`/patients${listQuery(filter)}`, { schema: PatientPageSchema });
  }

  patientSummary(): Promise<PatientSummary> {
    return request('/patients/summary', { schema: PatientSummarySchema });
  }

  patient(id: string): Promise<Patient> {
    return request(`/patients/${at(id)}`, { schema: PatientSchema });
  }

  async patientCases(patientId: string, filter: ClientCaseFilter): Promise<ClientCasesPage> {
    const result = await request(
      `/patients/${at(patientId)}/cases${queryOf(clientCaseQuery(filter))}`,
      { schema: ClientCasesPageSchema },
    );
    return result as unknown as ClientCasesPage;
  }

  addPatient(patient: NewPatient): Promise<Patient> {
    return request('/patients', {
      method: 'POST',
      body: patientBody(patient.name, patient.allowSameName),
      schema: PatientSchema,
    });
  }

  renamePatient(command: RenamePatientCommand): Promise<Patient> {
    return request(`/patients/${at(command.id)}`, {
      method: 'PATCH',
      body: patientBody(command.name, command.allowSameName),
      rowVersion: command.rowVersion,
      schema: PatientSchema,
    });
  }

  /** A 204, as deleting a doctor is. */
  async deletePatient(id: string): Promise<void> {
    await request(`/patients/${at(id)}`, { method: 'DELETE' });
  }
}
